import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Package,
  Building2,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { getDashboardStats } from '../../data/mockData';
import paths from '@/paths';

// Brand color for procurement demo
const BRAND_COLOR = '#6a89cc';

export function StatsOverview() {
  const stats = getDashboardStats();
  const navigate = useNavigate();

  const statCards = [
    {
      title: 'Total Inventory Units',
      value: stats.totalInventoryUnits.toLocaleString(),
      trend: { value: '+8.3%', direction: 'up' as const },
      icon: Package,
      onClick: () => {
        navigate({
          to: paths.authenticated.procurementDemo.inventory,
        });
      },
    },
    {
      title: 'Active Suppliers',
      value: stats.activeSuppliers.toString(),
      trend: { value: '+2', direction: 'up' as const },
      icon: Building2,
      onClick: () => {
        navigate({
          to: paths.authenticated.procurementDemo.suppliers,
        });
      },
    },
    {
      title: 'Low Stock Alerts',
      value: stats.lowStockAlerts.toString(),
      trend: {
        value: stats.lowStockAlerts > 0 ? `-${stats.lowStockAlerts}` : '0',
        direction: 'down' as const,
      },
      icon: AlertTriangle,
      onClick: () => {
        navigate({
          to: paths.authenticated.procurementDemo.inventory,
        });
      },
    },
    {
      title: 'Monthly Spending',
      value: '$284K',
      trend: { value: '+12.5%', direction: 'up' as const },
      icon: DollarSign,
      onClick: () => {
        navigate({
          to: paths.authenticated.procurementDemo.spendingAnalytics,
        });
      },
    },
  ];

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        const isClickable = !!stat.onClick;

        return (
          <Card
            key={index}
            className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group ${isClickable ? 'cursor-pointer' : ''}`}
            style={{
              borderColor: 'transparent',
            }}
            onClick={stat.onClick}
          >
            {/* Top gradient line on hover */}
            <div
              className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{
                background: `linear-gradient(to right, ${BRAND_COLOR}40, transparent)`,
              }}
            />
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div
                  className="p-3 rounded-xl"
                  style={{ backgroundColor: `${BRAND_COLOR}15` }}
                >
                  <Icon className="h-5 w-5" style={{ color: BRAND_COLOR }} />
                </div>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge
                      variant="secondary"
                      className={`text-xs font-medium cursor-help ${
                        stat.trend.direction === 'up'
                          ? 'bg-emerald-500/10 text-emerald-500'
                          : 'bg-rose-500/10 text-rose-500'
                      }`}
                    >
                      {stat.trend.direction === 'up' ? (
                        <TrendingUp className="h-3 w-3 mr-1" />
                      ) : (
                        <TrendingDown className="h-3 w-3 mr-1" />
                      )}
                      {stat.trend.value}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>vs last month</TooltipContent>
                </Tooltip>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold tracking-tight">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
